import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import {
  ExamInstanceEvaluationStatusEnum,
  ExamInstanceEvaluationStatusEnumLabels,
} from 'src/app/enums/exam-instance-evaluation-status.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ExamInstanceEvaluation } from 'src/app/models/exam_instance_evaluation.model';
import { ApiService, UtilsService } from 'src/app/services';

interface MyExamEvaluationRow extends ExamInstanceEvaluation {
  exam_name_label: string;
  status_label: string;
  score_label: string;
  has_imported_scanned_pdf: boolean;
  can_view_detail: boolean;
}

@Component({
  selector: 'app-upskilling-exams-tab',
  templateUrl: './upskilling-exams-tab.component.html',
  styleUrl: './upskilling-exams-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class UpskillingExamsTabComponent extends IndexTemplateComponent implements OnInit {
  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly utils_service = inject(UtilsService);

  dataSource = new MatTableDataSource<MyExamEvaluationRow>([]);

  displayedColumns: string[] = ['exam_name', 'status', 'score', 'created_at', 'actions'];

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    this.processUrlParams();

    const url_page = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (!url_page) {
      this.pageIndex = 0;
    }

    super.ngOnInit();
  }

  override fetchData(): void {
    const params: Record<string, unknown> = {
      page: this.pageIndex + 1,
      per_page: this.pageSize,
      order_by: 'created_at:desc',
      with: 'exam_instance',
      status: ExamInstanceEvaluationStatusEnum.Completed,
    };

    this.api_service
      .getMyExamInstanceEvaluationsPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<ExamInstanceEvaluation>): void => {
          this.dataSource.data = response.data.map(exam_instance_evaluation =>
            this.serializeExamInstanceEvaluation(exam_instance_evaluation)
          );
          this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  override changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchData();
    super.changePage(event);
  }

  downloadImportedScannedPdf(exam_instance_evaluation: MyExamEvaluationRow): void {
    if (!exam_instance_evaluation.has_imported_scanned_pdf) {
      return;
    }

    this.api_service
      .downloadExamInstanceEvaluationImportedPdf(exam_instance_evaluation.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: response => {
          const filename =
            response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') ??
            `examen_escaneado_${exam_instance_evaluation.id}.pdf`;

          this.utils_service.downloadDocumentFromBlob(response.body as Blob, filename);
        },
        error: (): void => {
          this.snack_bar.open('No se pudo descargar el examen escaneado.', undefined, { duration: 5000 });
        },
      });
  }

  private serializeExamInstanceEvaluation(exam_instance_evaluation: ExamInstanceEvaluation): MyExamEvaluationRow {
    return {
      ...exam_instance_evaluation,
      exam_name_label: exam_instance_evaluation.exam_instance?.name ?? '—',
      status_label: ExamInstanceEvaluationStatusEnumLabels[exam_instance_evaluation.status as ExamInstanceEvaluationStatusEnum] ?? '—',
      score_label:
        exam_instance_evaluation.score_over_ten !== null && exam_instance_evaluation.score_over_ten !== undefined
          ? `${exam_instance_evaluation.score_over_ten} / 10`
          : '—',
      has_imported_scanned_pdf: Boolean(exam_instance_evaluation.imported_pdf_storage_path),
      can_view_detail: exam_instance_evaluation.status === ExamInstanceEvaluationStatusEnum.Completed,
    };
  }

  private processUrlParams(): void {
    const url_per_page = this.activatedRoute.snapshot.queryParamMap.get('per_page');
    if (url_per_page) {
      this.pageSize = parseInt(url_per_page, 10);
    }

    const url_page = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (url_page) {
      this.pageIndex = parseInt(url_page, 10) - 1;
    }
  }
}
